import { useEffect, useState } from 'react';
import './HomePageModern.css';
import './ServicesPageModern.css';

const useCountUp = (target, duration = 1800, start = true) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const started = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - started) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return val;
};

const services = [
  {
    slug: 'full-service',
    title: 'Full Service Repair',
    price: '$997',
    tag: 'Most Popular',
    image: '/images/portfolio/services/igcconsulting.jpeg',
    description: 'Our flagship program. Complete credit overhaul with personalized dispute strategy, tradeline optimization, and funding prep.',
    cta: 'Enroll Today',
    href: 'https://stan.store/mrinternationalfl/p/full-service-w36mkvlq',
    emphasis: true,
  },
  {
    slug: 'smartcredit',
    title: 'SmartCredit',
    price: 'Monitoring',
    priceSmall: true,
    image: '/images/portfolio/services/smartcredit.png',
    description: 'Sign up for real-time credit monitoring. Know what is on your report before we start fixing it.',
    cta: 'Get Started',
    href: 'https://www.smartcredit.com/join/?pid=23470',
  },
  {
    slug: 'call',
    title: 'Book a 1:1 Call',
    price: '$150',
    image: '/images/portfolio/services/1on1call.webp',
    description: 'A private discovery call. Walk through your credit profile and leave with a custom action plan built for your situation.',
    cta: 'Book Your Call',
    href: 'https://stan.store/mrinternationalfl/p/book-a-11-call-with-me-mnbtgl5c',
  },
  {
    slug: 'inner-circle',
    title: 'Inner Circle Elite Mentorship',
    price: '$4,995',
    tag: 'VIP',
    tagGold: true,
    image: '/images/portfolio/services/1on1mentorship.jpeg',
    description: 'Direct 1-on-1 mentorship. Credit repair, funding access, and real estate leverage. The full playbook, taught personally.',
    cta: 'Join Today',
    href: 'https://stan.store/mrinternationalfl/p/inner-circle-1-on-1-elite-mentorship',
  },
];

const ServicesPageModern = ({ onNavigate, currentPage }) => {
  const fundedM = useCountUp(10, 1600);
  const liftPts = useCountUp(150, 2000);

  return (
    <div className="hpm spm">
      <div className="hpm-grain" aria-hidden="true"></div>
      <div className="hpm-mesh" aria-hidden="true">
        <div className="hpm-bloom hpm-bloom-1"></div>
        <div className="hpm-bloom hpm-bloom-2"></div>
        <div className="hpm-bloom hpm-bloom-3"></div>
      </div>

      <header className="hpm-header">
        <div className="hpm-header-inner">
          <div className="spm-header-left">
            <button className="hpm-logo" onClick={() => onNavigate('home')} aria-label="Home">
              <img src="/creditrepairlogo.png" alt="Infinite Growth Consulting" />
            </button>
            <button className="spm-back-nav" onClick={() => onNavigate('home')} aria-label="Back to home">
              <span className="spm-back-arrow">←</span>
              <span className="spm-back-text">Back</span>
            </button>
          </div>
          <nav className="hpm-nav">
            <button
              className={`hpm-navlink ${currentPage === 'services' ? 'active' : ''}`}
              onClick={() => onNavigate('services')}
            >
              Services
            </button>
            <a
              className="hpm-nav-cta"
              href="https://stan.store/mrinternationalfl/p/full-service-w36mkvlq"
              target="_blank"
              rel="noopener noreferrer"
            >
              Enroll Today
              <span className="hpm-arrow">→</span>
            </a>
          </nav>
        </div>
      </header>

      <section className="spm-hero">
        <div className="hpm-particles" aria-hidden="true">
          <span className="hpm-particle hpm-p-1">+82</span>
          <span className="hpm-particle hpm-pcream hpm-p-2">780</span>
          <span className="hpm-particle hpm-p-3">+150</span>
          <span className="hpm-particle hpm-p-4">850</span>
          <span className="hpm-particle hpm-pcream hpm-p-5">+200</span>
          <span className="hpm-particle hpm-pmuted hpm-p-6">$</span>
          <span className="hpm-particle hpm-p-7">+120</span>
          <span className="hpm-particle hpm-pmuted hpm-p-8">◆</span>
          <span className="hpm-particle hpm-pcream hpm-p-9">720</span>
          <span className="hpm-particle hpm-p-10">+175</span>
          <span className="hpm-particle hpm-pmuted hpm-p-11">▲</span>
          <span className="hpm-particle hpm-p-12">800</span>
        </div>
        <h1 className="spm-title">
          Transform Your <span className="hpm-serif-italic">Financial Future</span>
        </h1>
        <p className="spm-lede">
          Choose your path to credit excellence. Every tier is built to compound. Fix the score, access the capital, move into assets.
        </p>

        <dl className="hpm-hero-stats spm-stats">
          <div>
            <dt className="hpm-stat hpm-stat-green">${fundedM}M+</dt>
            <dd>Funded</dd>
          </div>
          <div>
            <dt className="hpm-stat hpm-stat-white">30-90</dt>
            <dd>Days</dd>
          </div>
          <div>
            <dt className="hpm-stat hpm-stat-gold">+{liftPts}</dt>
            <dd>Pts Avg. Lift</dd>
          </div>
        </dl>
      </section>

      <section className="spm-pricing">
        <div className="spm-pricing-grid">
          {services.map((s) => (
            <article key={s.slug} className={`spm-card ${s.emphasis ? 'spm-card-emph' : ''}`}>
              {s.tag && (
                <div className={`spm-card-tag ${s.tagGold ? 'spm-tag-gold' : ''}`}>
                  {s.tag}
                </div>
              )}
              <div className="spm-card-media">
                <img src={s.image} alt={s.title} />
              </div>
              <div className="spm-card-body">
                <div className="spm-card-head">
                  <h3>{s.title}</h3>
                  <span className={`spm-card-price ${s.priceSmall ? 'spm-card-price-sm' : ''}`}>
                    {s.price}
                  </span>
                </div>
                <p className="spm-card-desc">{s.description}</p>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hpm-btn-primary spm-card-cta"
                >
                  {s.cta}
                  <span className="hpm-arrow">→</span>
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="spm-contact">
        <div className="spm-contact-inner">
          <div className="hpm-label hpm-label-light">Not Sure Which Fits?</div>
          <h2 className="spm-contact-title">
            Let's <span className="hpm-serif-italic">Talk</span>
          </h2>
          <p className="spm-contact-sub">
            Send a direct message. We will build a plan tailored to your situation.
          </p>
          <a
            className="hpm-btn-primary hpm-btn-large"
            href="https://www.instagram.com/mrinternationalfl/"
            target="_blank"
            rel="noopener noreferrer"
          >
            DM on Instagram
            <span className="hpm-arrow">→</span>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ServicesPageModern;
